/*
 * Design: Swiss Precision × 和の余白
 * Brand: eightone (#E03030 accent, #1A1A1A headings, Noto Sans JP)
 * Mobile: カード余白最適化、価格表示調整、CTAタッチターゲット48px
 */

import { useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Rocket, GraduationCap, Server } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { scrollToContactForm } from "@/hooks/useScrollToContact";

const SERVICES = [
  {
    id: "starter",
    icon: Rocket,
    badge: "STARTER",
    title: "AI業務自動化スターターパック",
    catchcopy: "まずは1つ、AIで業務が自動化される感動体験を。",
    description: "最も課題と感じている定型業務を1つ特定し、ヒアリングから要件定義、AIエージェントの開発、実装までをワンストップで提供します。短期間で具体的な成果と成功体験を得られます。",
    price: "80",
    priceUnit: "万円（税別）から",
    period: "約1ヶ月〜",
    features: [
      "無料相談・業務ヒアリング",
      "要件定義・お見積り",
      "AIエージェント1体開発",
      "実装・操作レクチャー",
    ],
    subsidy: "デジタル化・AI導入補助金の活用で費用負担を大幅軽減",
    image: IMAGES.serviceAutomation,
    recommended: false,
  },
  {
    id: "training",
    icon: GraduationCap,
    badge: "RECOMMENDED",
    title: "現場主導DX・内製化支援プログラム",
    catchcopy: "AIを「使う」から「創る」へ。自走するDXチームを育成。",
    description: "Antigravityを使いこなし、お客様自身がAIエージェントを開発・運用できるスキルを習得するための、研修と伴走支援を組み合わせたプログラムです。",
    price: "150",
    priceUnit: "万円（税別）から",
    period: "3ヶ月〜",
    monthlyPrice: "16",
    monthlyUnit: "万円/月〜（伴走支援）",
    features: [
      "キックオフ・ゴール設定",
      "集合研修（基礎編＋実践編）",
      "OJT伴走支援（月1回〜）",
      "内製化・自走体制の構築",
    ],
    subsidy: "人材開発支援助成金の活用で研修費用の最大75%を助成",
    image: IMAGES.serviceTraining,
    recommended: true,
  },
  {
    id: "saas",
    icon: Server,
    badge: "SUBSCRIPTION",
    title: "業種特化型AIエージェント",
    catchcopy: "業界のベストプラクティスを、すぐにあなたの会社へ。",
    description: "製造業の「RFID在庫管理」、卸売業の「受発注処理」など、業種特化のAIエージェントをSaaS形式で提供。開発不要で、迅速に高度な自動化を始められます。",
    price: "50",
    priceUnit: "万円（税別）から",
    period: "初期設定",
    monthlyPrice: "5",
    monthlyUnit: "万円/月〜（利用料）",
    features: [
      "要件ヒアリング・適合診断",
      "初期設定・データ連携",
      "継続的アップデート",
      "運用サポート",
    ],
    subsidy: "クラウド利用料として最大2年間の補助対象",
    image: IMAGES.serviceSaas,
    recommended: false,
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

function TiltCard({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, scale: 1 });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({
      rotateX: (0.5 - y) * 5,
      rotateY: (x - 0.5) * 5,
      scale: 1.015,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ rotateX: 0, rotateY: 0, scale: 1 });
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
      style={{
        transform: `perspective(800px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) scale(${tilt.scale})`,
        transition: "transform 0.2s ease-out",
      }}
    >
      {children}
    </div>
  );
}

export function ServicesSection() {
  return (
    <section id="services" className="py-14 sm:py-20 lg:py-28 bg-white">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-10 sm:mb-16">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Services
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            3つのサービスメニュー
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-2xl leading-relaxed">
            お客様の状況に合わせて、最適なプランをお選びいただけます。すべてのプランで補助金・助成金の活用を前提に設計しています。
          </p>
        </motion.div>

        {/* Service Cards - Horizontal scroll on mobile, grid on desktop */}
        <div className="max-sm:-mx-4 max-sm:px-4 max-sm:overflow-x-auto max-sm:pb-4 scrollbar-hide">
          <div className="max-sm:flex max-sm:gap-4 max-sm:w-max sm:grid sm:grid-cols-1 lg:grid-cols-3 sm:gap-6 lg:gap-8">
            {SERVICES.map((service, i) => (
              <motion.div
                key={service.id}
                {...fadeInUp}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="max-sm:w-[85vw] max-sm:shrink-0"
              >
                <TiltCard
                  className={`relative bg-white border flex flex-col h-full ${
                    service.recommended
                      ? "border-[#E03030] shadow-[0_4px_32px_rgba(224,48,48,0.1)]"
                      : "border-[#E0E0E0] hover:border-[#E03030]"
                  } transition-all duration-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)]`}
                >
                  {/* Recommended badge */}
                  {service.recommended && (
                    <div className="absolute -top-3 left-4 sm:left-6 px-3 sm:px-4 py-1 bg-[#E03030] text-white text-xs font-bold tracking-wider z-10">
                      推奨
                    </div>
                  )}

                  {/* Image */}
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-5 sm:p-6 lg:p-8 flex flex-col flex-1">
                    {/* Badge */}
                    <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#1A1A1A] text-white rounded-sm">
                        <service.icon size={14} strokeWidth={1.5} className="sm:hidden" />
                        <service.icon size={16} strokeWidth={1.5} className="hidden sm:block" />
                      </div>
                      <span className="text-[10px] font-bold tracking-widest text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {service.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-1.5 sm:mb-2">
                      {service.title}
                    </h3>

                    {/* Catchcopy */}
                    <p className="text-xs sm:text-sm text-[#E03030] font-medium mb-2 sm:mb-3">
                      {service.catchcopy}
                    </p>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-[#333333] leading-relaxed mb-4 sm:mb-6">
                      {service.description}
                    </p>

                    {/* Price */}
                    <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-3 sm:p-4 mb-4 sm:mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#E03030]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {service.price}
                        </span>
                        <span className="text-xs sm:text-sm text-[#333333] font-medium">
                          {service.priceUnit}
                        </span>
                      </div>
                      {service.monthlyPrice && (
                        <div className="flex items-baseline gap-1 mt-2 pt-2 border-t border-[#E0E0E0]">
                          <span className="text-[10px] sm:text-xs text-[#666666]">＋月額</span>
                          <span className="text-lg sm:text-xl font-extrabold text-[#E03030]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {service.monthlyPrice}
                          </span>
                          <span className="text-[10px] sm:text-xs text-[#333333]">
                            {service.monthlyUnit}
                          </span>
                        </div>
                      )}
                      <div className="text-[10px] sm:text-xs text-[#666666] mt-2">
                        期間目安: {service.period}
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6 flex-1">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-xs sm:text-sm text-[#333333]">
                          <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-[#E03030] mt-1.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* Subsidy note */}
                    <div className="text-[10px] sm:text-xs text-[#666666] bg-[#FEF2F2] px-2.5 sm:px-3 py-2 rounded-sm mb-4 sm:mb-6">
                      {service.subsidy}
                    </div>

                    {/* CTA with pulse animation for recommended */}
                    <button
                      onClick={() => scrollToContactForm({ plan: service.id })}
                      className={`relative inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-3 text-sm font-bold rounded transition-colors duration-200 active:scale-[0.98] touch-manipulation ${
                        service.recommended
                          ? "bg-[#E03030] text-white hover:bg-[#c72828]"
                          : "bg-[#1A1A1A] text-white hover:bg-[#333333]"
                      }`}
                    >
                      {service.recommended && (
                        <span className="absolute inset-0 rounded bg-[#E03030] animate-ping opacity-20" />
                      )}
                      <span className="relative z-10 flex items-center gap-2">
                        このプランについて相談する
                        <ArrowRight size={14} />
                      </span>
                    </button>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
